import React, { useState } from 'react';
import { aiApi } from './api';
import StockLink from './StockLink';

export default function MarketResearch() {
  const [query, setQuery] = useState('');
  const [sector, setSector] = useState('');
  const [research, setResearch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const performResearch = async () => {
    if (!query.trim()) {
      setError('Please enter a research query');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await aiApi.marketResearch({ query, sector });
      setResearch(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      performResearch();
    }
  };

  return (
    <div className="module-container">
      <div className="module-header">
        <h2>Market Research</h2>
        <p>AI-powered market analysis and investment thesis discovery</p>
      </div>

      <div className="module-content">
        <div className="research-form">
          <div className="input-group">
            <label>Research Query</label>
            <textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="What would you like to research? e.g., 'What are the best AI stocks right now?'"
              rows={3}
            />
          </div>

          <div className="input-group">
            <label>Sector Filter (Optional)</label>
            <input
              type="text"
              value={sector}
              onChange={(e) => setSector(e.target.value)}
              placeholder="e.g., Technology, Healthcare, Finance"
            />
          </div>

          <button
            onClick={performResearch}
            disabled={loading}
            className="primary-button"
          >
            {loading ? 'Researching...' : 'Research Market'}
          </button>
        </div>

        {error && (
          <div className="error-box">
            <p className="error-icon">⚠</p>
            <p>{error}</p>
          </div>
        )}

        {research && (
          <div className="research-result">
            <section className="research-section">
              <h3>Executive Summary</h3>
              <p className="summary-text">{research.summary}</p>
            </section>

            {research.keyFindings && (
              <section className="research-section">
                <h3>Key Findings</h3>
                <ul className="findings-list">
                  {research.keyFindings.map((finding, idx) => (
                    <li key={idx}>
                      <span className="finding-number">{idx + 1}</span>
                      {finding}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {research.investmentTheses && (
              <section className="research-section">
                <h3>Investment Theses</h3>
                <div className="theses-grid">
                  {research.investmentTheses.map((thesis, idx) => (
                    <div key={idx} className="thesis-card">
                      {thesis}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {research.topPicks && (
              <section className="research-section">
                <h3>Top Picks</h3>
                <div className="picks-grid">
                  {research.topPicks.map((pick, idx) => (
                    <div key={idx} className="pick-card">
                      <StockLink symbol={pick.ticker} className="pick-ticker" />
                      <p className="pick-reasoning">{pick.reasoning}</p>
                      <div className="pick-metrics">
                        <span>Target: ${pick.targetPrice}</span>
                        <span className="pick-reward">{pick.riskReward}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {research.sectorOutlook && (
              <section className="research-section">
                <h3>Sector Outlook</h3>
                <p className="outlook-text">{research.sectorOutlook}</p>
              </section>
            )}

            {research.catalysts && (
              <section className="research-section">
                <h3>Market Catalysts</h3>
                <ul className="catalysts-list">
                  {research.catalysts.map((catalyst, idx) => (
                    <li key={idx}>{catalyst}</li>
                  ))}
                </ul>
              </section>
            )}

            <div className="research-confidence">
              <span>Confidence Level: </span>
              <strong>{research.confidence}%</strong>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
